export function normalizeYoutubeInput(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

function buildYoutubeEmbedUrl(
  videoId: string,
  searchParams: URLSearchParams
): string {
  const embedParams = new URLSearchParams();
  const list = searchParams.get("list");
  const start = searchParams.get("t") || searchParams.get("start");
  if (list) embedParams.set("list", list);
  if (start) embedParams.set("start", start);
  const query = embedParams.toString();
  return `https://www.youtube.com/embed/${videoId}${
    query ? `?${query}` : ""
  }`;
}

/** Parse share/watch URLs into iframe-safe YouTube embed URLs. */
export function getYoutubeEmbedUrl(url: string): string | null {
  const normalized = normalizeYoutubeInput(url);
  if (!normalized) return null;

  try {
    const u = new URL(normalized);
    const host = u.hostname.replace(/^www\./, "");

    if (host === "youtu.be" || host.endsWith(".youtu.be")) {
      const id = u.pathname.replace(/^\//, "").split("/")[0]?.split("?")[0];
      if (id) return buildYoutubeEmbedUrl(id, u.searchParams);
    }

    if (host.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return buildYoutubeEmbedUrl(v, u.searchParams);

      const shortMatch = u.pathname.match(/\/shorts\/([^/?]+)/);
      if (shortMatch?.[1]) {
        return buildYoutubeEmbedUrl(shortMatch[1], u.searchParams);
      }

      const embedMatch = u.pathname.match(/\/embed\/([^/?]+)/);
      if (embedMatch?.[1]) {
        return buildYoutubeEmbedUrl(embedMatch[1], u.searchParams);
      }

      if (u.pathname.startsWith("/embed/")) return normalized;
    }
  } catch {
    // fall through to regex fallback
  }

  const fallback = normalized.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/|v\/))([a-zA-Z0-9_-]{11})/
  );
  if (fallback?.[1]) {
    try {
      const u = new URL(normalized);
      return buildYoutubeEmbedUrl(fallback[1], u.searchParams);
    } catch {
      return `https://www.youtube.com/embed/${fallback[1]}`;
    }
  }

  return null;
}

export function isValidYoutubeUrl(url: string | undefined | null): boolean {
  if (!url?.trim()) return false;
  return getYoutubeEmbedUrl(url) !== null;
}
